//@flow
import * as React from 'react';
import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';
import HeaderCheckboxWithDropdown from './HeaderCheckboxWithDropdown';

import {
    StickyPositionEnum,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';

import { useSelector } from 'react-redux';

import isFunction from 'lodash/isFunction';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';

const useTableHandlers = ({
    props,
    toggleSelectAll,
    rowSelectedType,
    toggleSelectPage,
    disableRowSelection,
    isNewEntitiesTable
}: {
    props: {
        defaultColumns: Array<Object>,
        getExtraColumnProperties?: () => Array<Object>,
        entitySettingsName: string,
        customProcessColumns?: (Array<Object>) => Array<Object>,
        unitConverter: UnitConversionHelper
    },
    toggleSelectAll: (checked: boolean) => void,
    rowSelectedType: $Values<
        typeof HeaderCheckboxWithDropdown.ROWS_SELECTED_TYPE
    >,
    toggleSelectPage: (checked: boolean) => void,
    disableRowSelection: boolean,
    isNewEntitiesTable: boolean
}): ({
    columns: Array<Object>,
    onColumnFreeze: (id: string, frozen: boolean) => void,
    frozenColumns: Array<string>
}) => {
    const filterOutHiddenColumns = columns => {
        return columns.filter(column => {
            if (isFunction(column.isHidden)) {
                return !column.isHidden(props);
            }
            return true;
        });
    };

    const getExtraColumnProperties = () => {
        const { getExtraColumnProperties } = props;

        return [
            {
                id: 'selector',
                visible: true,
                headerText: () => {
                    return (
                        <HeaderCheckboxWithDropdown
                            rowSelectedType={rowSelectedType}
                            toggleSelectPage={toggleSelectPage}
                            toggleSelectAll={toggleSelectAll}
                            disabled={disableRowSelection}
                            isNewEntitiesTable={isNewEntitiesTable}
                        />
                    );
                },
                width: 60
            },
            ...(getExtraColumnProperties?.() || [])
        ];
    };

    const entitySettings = useSelector(state =>
        getEntitySettings(state, props)
    );

    const processColumns = columns => {
        const { entitySettingsName, customProcessColumns } = props;
        const extraColumnProperties = getExtraColumnProperties();

        let modifiedColumns = [...columns];
        if (customProcessColumns) {
            modifiedColumns = customProcessColumns(modifiedColumns);
        }
        modifiedColumns = filterOutHiddenColumns(modifiedColumns);

        modifiedColumns = CustomColumnHelpers.processColumns(
            entitySettings,
            modifiedColumns,
            entitySettingsName,
            extraColumnProperties,
            props.unitConverter
        );
        return modifiedColumns.map(column => {
            const { id, accessor, frozen } = column;
            return {
                ...column,
                frozen: id === 'selector' || frozen,
                sortable: false,
                columnId: `${id || accessor}`,
                fixed: frozen
                    ? column.fixedPosition || StickyPositionEnum.Left
                    : false,
                filterable:
                    id?.includes('code') ||
                    id?.includes('name') ||
                    id?.includes('teams.name') ||
                    id?.includes('teams.id') ||
                    false
            };
        });
    };

    const [frozenColumns, setFrozenColumns] = React.useState([]);
    const [columns, setColumns] = React.useState(() =>
        processColumns(props.defaultColumns)
    );

    React.useEffect(() => {
        setColumns(processColumns(props.defaultColumns));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowSelectedType, isNewEntitiesTable]);

    const onColumnFreeze = (id: string, frozen: boolean): void => {
        const frozenColumnLimit = FROZEN_COLUMN_LIMIT;
        let nextFrozenColumns = [];

        if (frozenColumns.length < frozenColumnLimit && frozen) {
            nextFrozenColumns = [...frozenColumns, id];
        } else if (!frozen) {
            nextFrozenColumns = frozenColumns.filter(_id => _id !== id);
        }

        const nextColumns = columns.map(column => {
            const { columnId, frozen: prevFrozen } = column;
            const currFreezeState = columnId === id ? frozen : prevFrozen;
            const modifiedCol = {
                ...column,
                frozen: currFreezeState,
                fixed: currFreezeState
                    ? column.fixedPosition || StickyPositionEnum.Left
                    : false
            };
            return modifiedCol;
        });
        setColumns(nextColumns);
        setFrozenColumns(nextFrozenColumns);
    };

    return { onColumnFreeze, columns, frozenColumns };
};

export default useTableHandlers;
